import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  getJournalBySlug,
  getUserProgress,
  getDayContent,
  getDayEntry,
  saveDayEntry,
  markDayComplete,
  getReferenceSections,
} from "../services/moneyMagicService";
import MicroWinModal from "../components/MicroWinModal";
import "./MoneyMagicDay.css";

const getSessionFromStorage = () => {
  try {
    const storageKey = Object.keys(localStorage).find(
      (k) => k.includes("sb-") && k.includes("-auth-token")
    );
    if (!storageKey) return null;
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { userId: parsed?.user?.id };
  } catch {
    return null;
  }
};

function UntangleDay() {
  const { dayNumber: dayParam } = useParams();
  const navigate = useNavigate();
  const dayNumber = parseInt(dayParam, 10);

  const [loading, setLoading] = useState(true);
  const [dayContent, setDayContent] = useState(null);
  const [progress, setProgress] = useState(null);
  const [referenceSections, setReferenceSections] = useState([]);
  const [journalId, setJournalId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [hasMicroWins, setHasMicroWins] = useState(false);
  const [totalDays, setTotalDays] = useState(30);

  const [journalResponse, setJournalResponse] = useState("");
  const [creativeResponse, setCreativeResponse] = useState("");

  const [saveStatus, setSaveStatus] = useState("idle");

  const [completing, setCompleting] = useState(false);
  const [dayCompleted, setDayCompleted] = useState(false);

  const [showMicroWin, setShowMicroWin] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  const savedResponsesRef = useRef({ journal: "", creative: "" });
  const saveTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    // Redirect invalid day numbers after we know totalDays — checked again below once loaded
    if (isNaN(dayNumber) || dayNumber < 1) {
      navigate("/journal/the-untangle-journal", { replace: true });
      return;
    }

    setLoading(true);
    setDayCompleted(false);
    setCompleting(false);
    setSaveStatus("idle");
    setExpandedSection(null);
    setJournalResponse("");
    setCreativeResponse("");
    savedResponsesRef.current = { journal: "", creative: "" };

    let alive = true;

    const load = async () => {
      const session = getSessionFromStorage();
      if (!session?.userId) {
        navigate("/login", { replace: true });
        return;
      }

      const journalData = await getJournalBySlug("the-untangle-journal");
      if (!alive) return;
      if (!journalData) {
        navigate("/journal/the-untangle-journal", { replace: true });
        return;
      }

      const journalTotalDays = journalData.total_days ?? 30;

      // Now that we have total_days, validate the URL param
      if (dayNumber > journalTotalDays) {
        navigate("/journal/the-untangle-journal", { replace: true });
        return;
      }

      const [userProgress, content, entry, refs] = await Promise.all([
        getUserProgress(session.userId, journalData.id),
        getDayContent(journalData.id, dayNumber),
        getDayEntry(session.userId, journalData.id, dayNumber),
        getReferenceSections(journalData.id),
      ]);
      if (!alive) return;

      if (!userProgress) {
        navigate("/journal/the-untangle-journal/intro", { replace: true });
        return;
      }

      if (dayNumber > userProgress.currentDay) {
        navigate(`/journal/the-untangle-journal/day/${userProgress.currentDay}`, {
          replace: true,
        });
        return;
      }

      if (!content) {
        navigate(`/journal/the-untangle-journal/day/${userProgress.currentDay}`, {
          replace: true,
        });
        return;
      }

      const jr = entry?.journalResponse ?? "";
      const cr = entry?.creativeResponse ?? "";

      setUserId(session.userId);
      setJournalId(journalData.id);
      setHasMicroWins(journalData.has_micro_wins ?? false);
      setTotalDays(journalTotalDays);
      setProgress(userProgress);
      setDayContent(content);
      setReferenceSections(refs);
      setJournalResponse(jr);
      setCreativeResponse(cr);
      savedResponsesRef.current = { journal: jr, creative: cr };

      setLoading(false);
    };

    load();

    return () => {
      alive = false;
    };
  }, [dayNumber, navigate]);

  const handleAutoSave = async (jr, cr) => {
    if (!userId || !journalId) return;
    if (
      jr === savedResponsesRef.current.journal &&
      cr === savedResponsesRef.current.creative
    )
      return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus("saving");

    const ok = await saveDayEntry(userId, journalId, dayNumber, {
      journalResponse: jr,
      creativeResponse: cr,
    });

    if (ok) {
      savedResponsesRef.current = { journal: jr, creative: cr };
      setSaveStatus("saved");
      saveTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
    } else {
      setSaveStatus("error");
      saveTimerRef.current = setTimeout(() => setSaveStatus("idle"), 3000);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    const ok = await markDayComplete(
      userId,
      journalId,
      dayNumber,
      { journalResponse, creativeResponse },
      totalDays
    );
    if (!ok) {
      setCompleting(false);
      return;
    }
    savedResponsesRef.current = { journal: journalResponse, creative: creativeResponse };

    if (dayNumber >= totalDays) {
      navigate("/journal/the-untangle-journal/snapshot/after");
    } else {
      setCompleting(false);
      setDayCompleted(true);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        navigate("/journal/the-untangle-journal");
      }, 1400);
    }
  };

  const handleUpdatePastDay = async () => {
    if (!userId || !journalId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus("saving");
    const ok = await saveDayEntry(userId, journalId, dayNumber, {
      journalResponse,
      creativeResponse,
    });
    if (ok) {
      savedResponsesRef.current = { journal: journalResponse, creative: creativeResponse };
      setSaveStatus("saved");
      saveTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
    } else {
      setSaveStatus("error");
      saveTimerRef.current = setTimeout(() => setSaveStatus("idle"), 3000);
    }
  };

  if (loading) {
    return (
      <div className="mmd-gate">
        <p className="mmd-gate-text">Opening your journal…</p>
      </div>
    );
  }

  const isPastDay = dayNumber < (progress?.currentDay ?? 1);
  const isCurrentDay = dayNumber === (progress?.currentDay ?? 1);

  // No em-dash transforms — Untangle content uses em dashes inline, not as paragraph breaks
  const processField = (text) => text || "";

  const processedFocusText        = processField(dayContent.focusText);
  const processedCreativeActivity = processField(dayContent.creativeActivity);
  const processedJournalPrompt    = processField(dayContent.journalPrompt);
  const processedMiniRitual       = processField(dayContent.miniRitual);
  const processedPsychologyNote   = processField(dayContent.psychologyNote);

  return (
    <div className="mmd-page">
      <div className="mmd-layout">

        {/* ── Main column ─────────────────────────────────────────────────── */}
        <main className="mmd-main">

          <button
            className="mmd-breadcrumb"
            type="button"
            onClick={() => navigate("/journal/the-untangle-journal")}
          >
            ← Your Journey
          </button>

          {/* Day header */}
          <header className="mmd-header">
            <div className="mmd-header-meta">
              <span className="mmd-day-label">Day {dayNumber}</span>
              <span className="mmd-day-count">{dayNumber} / {totalDays}</span>
            </div>
            <h1 className="mmd-day-title">{dayContent.title}</h1>
            {dayContent.focusText && (
              <div className="mmd-focus-text">
                <ReactMarkdown>{processedFocusText}</ReactMarkdown>
              </div>
            )}
          </header>

          {saveStatus !== "idle" && (
            <p className={`mmd-save-badge mmd-save-badge--${saveStatus}`}>
              {saveStatus === "saving" && "Saving…"}
              {saveStatus === "saved" && "✓ Saved"}
              {saveStatus === "error" && "Could not save — check your connection"}
            </p>
          )}

          {/* ── 1. Spell ───────────────────────────────────────────────── */}
          <section className="mmd-card">
            <div className="mmd-card-header">
              <span className="mmd-section-icon" aria-hidden="true">🔮</span>
              <h2 className="mmd-section-heading">Today's Spell</h2>
              {dayContent.spellId && (
                <button
                  className="mmd-spell-link"
                  type="button"
                  onClick={() => navigate(`/spell/${dayContent.spellId}`)}
                >
                  Open this spell in the library →
                </button>
              )}
            </div>
            <div className="mmd-prompt-body mmd-md">
              <ReactMarkdown>{processedCreativeActivity}</ReactMarkdown>
            </div>
            <textarea
              className="mmd-textarea"
              value={creativeResponse}
              onChange={(e) => setCreativeResponse(e.target.value)}
              onBlur={() => handleAutoSave(journalResponse, creativeResponse)}
              placeholder="Note what came up, what you felt, what shifted…"
              rows={5}
              aria-label="Spell notes"
            />
          </section>

          {/* ── 2. Journal Prompt ─────────────────────────────────────── */}
          <section className="mmd-card">
            <div className="mmd-card-header">
              <span className="mmd-section-icon" aria-hidden="true">📝</span>
              <h2 className="mmd-section-heading">Journal Prompt</h2>
            </div>
            <div className="mmd-prompt-body mmd-md">
              <ReactMarkdown>{processedJournalPrompt}</ReactMarkdown>
            </div>
            <textarea
              className="mmd-textarea"
              value={journalResponse}
              onChange={(e) => setJournalResponse(e.target.value)}
              onBlur={() => handleAutoSave(journalResponse, creativeResponse)}
              placeholder="Write freely — this is your space…"
              rows={9}
              aria-label="Journal response"
            />
          </section>

          {/* ── 3. Psychology Note ─────────────────────────────────────── */}
          {dayContent.psychologyNote && (
            <aside className="mmd-psychology">
              <span className="mmd-psychology-icon" aria-hidden="true">🧠</span>
              <div className="mmd-psychology-body mmd-md">
                <ReactMarkdown>{processedPsychologyNote}</ReactMarkdown>
              </div>
            </aside>
          )}

          {/* ── 4. Mini-Ritual ─────────────────────────────────────────── */}
          <section className="mmd-card mmd-card--ritual">
            <div className="mmd-card-header">
              <span className="mmd-section-icon" aria-hidden="true">✨</span>
              <h2 className="mmd-section-heading">Mini-Ritual</h2>
            </div>
            <div className="mmd-ritual-body mmd-md">
              <ReactMarkdown>{processedMiniRitual}</ReactMarkdown>
            </div>
          </section>

          {/* ── Action row ─────────────────────────────────────────────── */}
          <div className="mmd-actions">
            {isCurrentDay && !dayCompleted && (
              <button
                className="mmd-complete-btn"
                type="button"
                onClick={handleComplete}
                disabled={completing}
              >
                {completing
                  ? "Saving…"
                  : dayNumber === totalDays
                  ? "Complete the Journey ✦"
                  : "Mark Day Complete →"}
              </button>
            )}

            {isCurrentDay && dayCompleted && (
              <div className="mmd-day-done">
                <span className="mmd-day-done-mark">✦</span>
                Day {dayNumber} complete — see you tomorrow.
              </div>
            )}

            {isPastDay && (
              <button
                className="mmd-update-btn"
                type="button"
                onClick={handleUpdatePastDay}
                disabled={saveStatus === "saving"}
              >
                {saveStatus === "saving" ? "Saving…" : "Update Responses"}
              </button>
            )}
          </div>

        </main>

        {/* ── Sidebar ─────────────────────────────────────────────────────── */}
        <aside className="mmd-sidebar">

          {dayContent.spellId && (
            <div className="mmd-spell-sidebar-card">
              <p className="mmd-spell-sidebar-label">
                Day {dayNumber} — {dayContent.title}
              </p>
              {dayContent.spellName && (
                <p className="mmd-spell-sidebar-name">{dayContent.spellName}</p>
              )}
              <button
                className="mmd-spell-sidebar-link"
                type="button"
                onClick={() => navigate(`/spell/${dayContent.spellId}`)}
              >
                Open in the library →
              </button>
            </div>
          )}

          {hasMicroWins && (
            <button
              className="mmd-sidebar-btn mmd-sidebar-btn--win"
              type="button"
              onClick={() => setShowMicroWin(true)}
            >
              <span>✦</span>
              Add a Micro-Win
            </button>
          )}

          {referenceSections.map((section) => (
            <div key={section.id} className="mmd-ref-block">
              <button
                className={`mmd-sidebar-btn ${
                  expandedSection === section.id ? "mmd-sidebar-btn--open" : ""
                }`}
                type="button"
                onClick={() =>
                  setExpandedSection((prev) =>
                    prev === section.id ? null : section.id
                  )
                }
              >
                <span>📖</span>
                {section.title}
                <span className="mmd-chevron">
                  {expandedSection === section.id ? "▲" : "▼"}
                </span>
              </button>

              {expandedSection === section.id && (
                <div className="mmd-ref-content mmd-md">
                  <ReactMarkdown>{section.content || ""}</ReactMarkdown>
                </div>
              )}
            </div>
          ))}

        </aside>
      </div>

      {hasMicroWins && showMicroWin && (
        <MicroWinModal
          userId={userId}
          journalId={journalId}
          dayNumber={dayNumber}
          onClose={() => setShowMicroWin(false)}
        />
      )}
    </div>
  );
}

export default UntangleDay;
